/*
    This is Data Processing layer. As a result of it we get the source object that will be used in with view components
*/
import VisualComponent from './VisualComponent';

export default class ArticleStore extends VisualComponent {
    constructor(config, processors = {}, options = {}) {
        super();
        if (typeof ArticleStore.instance === 'object') {
            // singleton-ing this class as it is expected to manage a single article page... but just for fun really
            return ArticleStore.instance;
        }

        ArticleStore.instance = this;

        this.article = document.querySelector(config.selectors.article);
        this.config = config;
        if (this.article) {
            this._processors = Object.keys(processors).length ? {...this._processors, ...processors} : this._processors;
        }
        return this;
    }

    initiate() {
        if (this.article) {
            this._manipulateData.session();
            this.data = this._processData();
        }
        return this;
    }

    shouldBeExcluded(obj, config) {
        // some clients like to exclude certain figures from the figure viewer/collateral,
        // so those figures should not be processed (e.g. AAAS)
        return (
            config.preventFor?.enabled && obj.getAttribute(config.preventFor?.attribute) === config.preventFor?.values
        );
    }

    _figureProcessor = {
        _graphic: [],
        _table: [],
        _media: [],
        _other: [],

        getDocumentFigures: (raw, selectors) => {
            // Grouped figures should be treated as a group and not as individual figures, so we should
            // create an array of standalone <figure> elements that are not nested within a <figure role="group">.
            return [...raw.querySelectorAll(selectors.figure)].filter(fig => {
                const groupSelector =
                    selectors.figure + '[' + selectors.grouped.attribute + '="' + selectors.grouped.value + '"]';
                return (
                    fig.getAttribute(selectors.grouped.attribute) === selectors.grouped.value ||
                    !fig.closest(groupSelector)
                );
            });
        },

        handleFigure: (obj, config, order, selectors) => {
            const props = {content: '', contentRef: []};

            const cloned = obj.cloneNode(true);
            const figIds = cloned.querySelectorAll('[id]');
            figIds.forEach(el => {
                const points = cloned.querySelectorAll('[href="#' + el.id + '"]');
                if (points.length === 0) {
                    // there is no point keeping an id that is not referenced here and risk invalid HTML with duplicate ids
                    el.removeAttribute('id');
                } else {
                    el.id.replace(this.config.enhancements.prefix, '');
                    el.id = this.config.figureViewer.s.prefix + '-' + el.id;
                    points.forEach(point => {
                        point.href = '#' + el.id;
                    });
                }
            });

            if (cloned.getAttribute(selectors.grouped.attribute) !== selectors.grouped.value) {
                const content = obj.querySelectorAll(config.contentSelector);
                content.forEach(content => (props.content += content.outerHTML));
                props.contentRef = content[0];
            }

            const notes = obj.querySelector(selectors.notes);
            props.notes = notes?.outerHTML;

            let figcaption = cloned.querySelector(selectors.caption);
            let caption = '';
            if (figcaption) {
                caption = figcaption?.outerHTML;
            } else {
                figcaption = [...cloned.childNodes].filter(child => child.tagName === 'FIGCAPTION');
                figcaption.forEach(data => {
                    data.classList.remove('sr-only');
                    data.querySelector(config.toolsSelector)?.remove();
                    if (!data.querySelector(selectors.notes)) {
                        caption += data.innerHTML;
                    }
                });
            }
            caption = this.defaultProcessorDescriptionCustom(cloned, caption, config.class);

            const tempTitle = (obj.closest(selectors.parentSelector) || obj).querySelector(
                config.titleSelector
            )?.innerHTML;
            let title = this.defaultProcessorTitleCustom(obj, tempTitle, config.class);
            if (!title && !caption && config.fillInMissingData?.enabled) {
                const isGraphicalbstract = obj.closest('#graphical-abstract');
                if (isGraphicalbstract) {
                    const sectionTitle = isGraphicalbstract.querySelector('h2, h3');
                    title = sectionTitle ? sectionTitle.innerHTML : 'Graphical abstract';
                } else {
                    title = config.fillInMissingData?.title + ' ' + order;
                }
            }

            // this originated from EMBO, they have the links straight in the HTML, no need to construct them
            const tools = obj.querySelector(config.toolsSelector)?.querySelectorAll('a');
            if (tools && tools.length > 0) {
                props.download = {};
                tools.forEach(tool => {
                    let type = undefined;
                    switch (tool.className) {
                        case 'core-table-download-asset':
                        case 'core-figure-download-asset':
                            type = 'img';
                            break;
                        case 'core-table-download-powerpoint':
                        case 'core-figure-download-powerpoint':
                            type = 'ppt';
                            break;
                        case 'core-table-download-source-data':
                        case 'core-figure-download-source-data':
                            type = 'srcData';
                            break;
                        default:
                            break;
                    }
                    if (!props.download[type]) {
                        props.download[type] = [];
                    }
                    props.download[type].push({
                        href: tool.href,
                        label: tool.innerHTML,
                    });
                });
            }

            if (obj.id) {
                props.linkRefs = this.article.querySelectorAll('[href="#' + obj.id + '"]');
            } else {
                obj.id = 'core-id-' + self.crypto.randomUUID();
            }

            return {
                id: obj.id,
                caption,
                title,
                ref: obj,
                ...props,
            };
        },
        handleGraphics: (obj, order, config, selectors, array = this._figureProcessor._graphic) => {
            if (this.shouldBeExcluded(obj, config)) return;

            let tempObj = obj.cloneNode(true);
            const props = this._figureProcessor.handleFigure(obj, config, order, selectors);
            let imageData = [...tempObj.childNodes].filter(child => child.tagName === 'IMG');
            if (imageData.length === 0) {
                // APP customization, but we can assume others have it too... maybe move class to config file
                tempObj = tempObj.querySelector('.graphic-wrap');
                if (tempObj) {
                    imageData = [...tempObj.childNodes].filter(child => child.tagName === 'IMG');
                }
            }
            if (imageData.length === 0) {
                if (obj.getAttribute(selectors.grouped.attribute) === selectors.grouped.value) {
                    const multiFigure = [];
                    obj.querySelectorAll(selectors.figure).forEach(figure => {
                        this._figureProcessor.handleGraphics(figure, order, config, selectors, multiFigure);
                    });
                    props.multiFigure = multiFigure;
                } else {
                    return;
                }
            } else if (imageData.length > 1) {
                const multiFigure = [];
                [...obj.childNodes]
                    .filter(child => child.tagName === 'IMG')
                    .forEach((img, index) => {
                        const span = document.createElement('span');
                        span.innerHTML = img.outerHTML;
                        span.dataset['open'] = 'viewer';
                        span.dataset['index'] = index.toString();
                        span.dataset['target'] = 'core-fv-' + obj.id;
                        img.parentNode.replaceChild(span, img);
                    });
                obj.querySelectorAll('[data-open]').forEach((figure, index) => {
                    const newConf = {...config};
                    newConf.fillInMissingData.enabled = false;
                    newConf.titleSelector = null;
                    this._figureProcessor.handleGraphics(figure, order, newConf, selectors, multiFigure);
                    if (!multiFigure[index].download) {
                        multiFigure[index].download = props.download;
                    }
                });
                props.multiFigure = multiFigure;
            } else {
                imageData.forEach(img => {
                    const clone = img.cloneNode(true);
                    clone.removeAttribute('aria-labelledby');
                    clone.removeAttribute('data-viewer-src');
                    clone.removeAttribute('data-thumbnail-src');
                    clone.setAttribute('aria-hidden', 'true');
                    clone.setAttribute('draggable', 'false');
                    props.modifiedContent = clone.outerHTML;
                });
                props.figureType = 'image';
                props.src = {
                    thumbnail: imageData[0].dataset && imageData[0].dataset['thumbnailSrc'],
                    medium: imageData[0].src,
                    large: imageData[0].dataset && imageData[0].dataset['viewerSrc'],
                };

                const unhandledData = [...tempObj.childNodes].filter(
                    child => child.tagName !== 'FIGCAPTION' && child.tagName !== 'IMG'
                );
                if (unhandledData.length > 0) {
                    let data = '';
                    unhandledData.forEach(element =>
                        element.outerHTML !== props.notes && element.outerHTML !== obj.querySelector(config.toolsSelector).outerHTML
                            ? (data += element.outerHTML)
                            : ''
                    );
                    if (data !== '') props.unhandledData = data;
                }
            }
            props.type = config.class;
            array.push(props);
        },
        handleTables: (obj, order, config, selectors, array = this._figureProcessor._table) => {
            if (this.shouldBeExcluded(obj, config)) return;

            const props = this._figureProcessor.handleFigure(obj, config, order, selectors);
            const tablesData = obj.querySelectorAll(selectors.table);
            props.figureType = 'html';
            props.smartCandidate = true;
            props.type = config.class;
            if (obj.getAttribute(selectors.grouped.attribute) === selectors.grouped.value) {
                const multiFigure = [];
                obj.querySelectorAll(selectors.figure).forEach(table => {
                    this._figureProcessor.handleTables(table, order, config, selectors, multiFigure);
                });
                props.multiFigure = multiFigure;
            } else {
                /* if we want the tables to be enhanced by our code later, this is the place to estimate the candidate */
                tablesData.forEach(table => {
                    const spannedElements = table.querySelectorAll('[rowspan], [colspan]');
                    spannedElements.forEach(el => {
                        if (el.tagName === 'TD') {
                            props.smartCandidate = false;
                        }
                    });
                    if (table.querySelectorAll('tbody tr').length < config.smartTables.rowLimit) {
                        props.smartCandidate = false;
                    }
                });
            }
            array.push(props);
        },
        handleMedia: (obj, order, config, selectors, array = this._figureProcessor._media) => {
            if (this.shouldBeExcluded(obj, config)) return;

            if (!obj.id) {
                obj.id = 'core-id-' + self.crypto.randomUUID()
            }
            const tempObj = obj.cloneNode(true);
            const host = obj.getAttribute('data-host') || 'default';
            const playerConfig = this.config.videoPlayer[host];
            const tempConfig = this.deepMerge(config, {contentSelector: playerConfig.selector});
            if (playerConfig.wrapperDivClass) {
                tempConfig.contentSelector = '.' + playerConfig.wrapperDivClass;
            }

            const video = tempObj.querySelector(playerConfig.selector);
            if (!video) return;

            if (video.id) {
                video.id += this.config.enhancements.suffix;
            }
            const props = this._figureProcessor.handleFigure(tempObj, tempConfig, order, selectors);
            const videoSrc = (attrs, video, original_src, host) => {
                switch (host) {
                    case 'brightcove':
                        attrs['src_original'] = original_src;
                        attrs[
                            'src'
                        ] = `${playerConfig.src}/${video.dataset.account}/${video.dataset.player}_default/index.html?videoId=${video.dataset.videoId}`;
                        break;
                    case 'cadmore':
                        attrs['src_original'] = original_src;
                        attrs['src'] = original_src += playerConfig.configuratorId;
                        break;
                    default:
                        attrs['src'] = original_src;
                        break;
                }
            };
            let foundSrc = false;
            const attributeNodeArray = [...video?.attributes];
            const attrs = attributeNodeArray.reduce((attrs, attribute) => {
                switch (attribute.name) {
                    case 'src':
                        videoSrc(attrs, video, attribute.value, host);
                        foundSrc = true;
                        break;
                    case 'allowfullscreen':
                        attrs['allowFullscreen'] = attribute.value;
                        break;
                    case 'class':
                        break;
                    default:
                        attrs[attribute.name] = attribute.value;
                        break;
                }
                return attrs;
            }, {});
            if (!foundSrc) videoSrc(attrs, video, '#', host);
            props.host = host;
            props.type = config.class;
            props.videoObj = attrs;
            props.figureType = 'html';

            if (!props.caption) {
                let previous = obj.previousSibling || obj.parentNode.previousSibling;
                props.caption = previous ? previous.innerHTML : null;
            }

            array.push(props);
        },
        handleOther: (obj, order, config, selectors, array = this._figureProcessor._other) => {
            if (this.shouldBeExcluded(obj, config)) return;
            array.push({
                ...this._figureProcessor.handleFigure(obj, config, order, selectors),
                id: obj.id,
                content: obj.outerHTML,
                ref: obj,
            });
        },

        processFigures: () => {
            let {graphic, table, media, other, selectors, all} = this.config.figures;
            graphic = this.deepMerge(all, graphic);
            table = this.deepMerge(all, table);
            media = this.deepMerge(all, media);
            other = this.deepMerge(all, other);

            let graphicIndex = 0,
                tableIndex = 0,
                mediaIndex = 0,
                otherIndex = 0;
            let data = this._figureProcessor.getDocumentFigures(this.article, selectors);
            data = data.concat([...this.article.querySelectorAll(media.customSelector)]);

            data.forEach(obj => {
                switch (obj.className || obj.getAttribute('data-type') || obj.parentNode.getAttribute('data-type')) {
                    case 'graphic-wrap-group':
                    case graphic.class:
                        if (obj.querySelectorAll(media.contentSelector)?.length > 0) {
                            mediaIndex++;
                            this._figureProcessor.handleMedia(obj, mediaIndex, media, selectors);
                        } else {
                            graphicIndex++;
                            this._figureProcessor.handleGraphics(obj, graphicIndex, graphic, selectors);
                        }
                        break;
                    case 'table-wrap-group':
                    case table.class:
                        tableIndex++;
                        const tablesData = obj.querySelectorAll(selectors.table);
                        if (tablesData.length === 0) {
                            this._figureProcessor.handleGraphics(
                                obj,
                                tableIndex,
                                table,
                                selectors,
                                this._figureProcessor._table
                            );
                        } else {
                            this._figureProcessor.handleTables(obj, tableIndex, table, selectors);
                        }
                        break;
                    case media.class:
                        mediaIndex++;
                        this._figureProcessor.handleMedia(obj, mediaIndex, media, selectors);
                        break;
                    default:
                        const isTextNode = obj.classList.contains('boxed-text') || obj.classList.contains('statement');
                        if (!!obj.querySelectorAll(media.contentSelector) && !isTextNode) {
                            // sometimes we get no proper wrap (PNAS - LIT-606212) but it's still a video. TODO: Is it better to amend the XML?
                            mediaIndex++;
                            this._figureProcessor.handleMedia(obj, mediaIndex, media, selectors);
                        } else {
                            otherIndex++;
                            this._figureProcessor.handleOther(obj, otherIndex, other, selectors);
                        }
                        break;
                }
            });

            return {
                graphic: this._figureProcessor._graphic,
                table: this._figureProcessor._table,
                media: this._figureProcessor._media,
                other: this._figureProcessor._other,
            };
        },
    };

    _processors = {
        citations: () => {
            try {
                const config = this.config.citations;
                if (
                    config.pointTo === 'collateral' &&
                    !document.body.querySelector('#' + this.config.collateral.s.paneIds.citations)
                ) {
                    config.pointTo = 'body';
                }
                return this._manipulateData.xrefLinks(config);
            } catch (e) {
                console.debug('Processors - citations: ' + e);
            }
        },
        footnotes: () => {
            try {
                return this._manipulateData.xrefLinks(this.config.footnotes);
            } catch (e) {
                console.debug('Processors - footnotes: ' + e);
            }
        },
        // Order matters! Make sure that figures are always after citations and footnote for proper XREF processing
        figures: () => {
            try {
                return this._figureProcessor.processFigures();
            } catch (e) {
                console.debug('Processors - figures: ' + e);
            }
        },
        navigation: raw => {
            try {
                return raw.querySelector(this.config.selectors.navigation);
            } catch (e) {
                console.debug('Processors - navigation: ' + e);
            }
        },
        sections: raw => {
            try {
                return Array.prototype.slice
                    .call(raw.querySelectorAll(this.config.selectors.sections))
                    .reduce((acc, o) => {
                        if (o.hash) {
                            const sectionParent = document.getElementById(o.hash.replace('#', ''));
                            // #body.sec5 is a typical id and impossible to query other than with getElementById
                            if (sectionParent) {
                                const title = sectionParent.querySelector('h2,h3,h4,h5,h6');
                                if (title) {
                                    title.sectionParent = sectionParent;
                                    acc.push(title);
                                }
                            }
                        }
                        return acc;
                    }, []);
            } catch (e) {
                console.debug('Processors - sections: ' + e);
            }
        },
        decorationsPosRef: raw => raw.querySelector(this.config.selectors.decorationsXReference), // TODO is it used anywhere?
    };

    _manipulateData = {
        xrefLinks: config => {
            const temp = this.article.querySelectorAll(config.s.item);
            return Array.prototype.slice.call(temp).map(link => {
                const populatePoints = (array, id) => {
                    const points = [];
                    array.forEach((point, index) => {
                        /*
                         * if the in-document target is hidden, we must fall back to the figure viewer (LIT-508542)
                         * else use whatever the item's config instructs
                         */
                        const isHidden = this.isHidden(point);
                        const prefix = (config.pointTo === 'body' ? config : this.config[config.pointTo]).s.prefix;
                        const tempId = id + '-' + Number(index + 1);

                        if (!point.href.includes(prefix)) {
                            // if we haven't encountered this xref yet
                            if (config.pointTo !== 'body' && !isHidden) {
                                point.id = config.s.prefix + tempId;
                                point.href = '#' + prefix + point.href.split('#')[1];
                            } else if (config.pointTo === 'body' && !point.id) {
                                point.id = config.s.prefix + tempId;
                            }
                        }

                        points.push({
                            ref: point,
                            href: isHidden ? '#' + this.config['figureViewer'].s.prefix + '-' + tempId : '#' + point.id,
                            openInViewer: isHidden,
                            content: this._manipulateData.xrefSnippet(point, index),
                        });
                    });
                    return points;
                };
                let id = link.id;
                let array = document.querySelectorAll('[' + config.s.point + id + '"]');
                if (!id || array.length === 0) {
                    id = link.parentElement.id;
                    array = document.querySelectorAll('[' + config.s.point + id + '"]');
                }
                return {
                    ref: link,
                    id,
                    points: !id || array.length === 0 ? [] : populatePoints(array, id),
                };
            });
        },
        xrefSnippet: (anchor, key) => {
            let prevSibling = anchor.previousSibling;
            let snippet = '';
            const dataConfig = this.config.enhancements;

            while (prevSibling?.tagName === 'A' || prevSibling?.textContent.length <= 3) {
                prevSibling = prevSibling?.previousSibling;
            }
            let content = prevSibling?.textContent;
            if (!content) {
                prevSibling = anchor.parentNode?.previousSibling;
                content = prevSibling?.textContent;
            }
            if (!content) {
                snippet = anchor.textContent;
            } else {
                // remove trailing parenthesis
                let lastChar = content.slice(-1);
                if (lastChar === '(' || lastChar === '[') {
                    content = content.slice(0, -1);
                }

                // if after removing trailing "(" the string is too small, fetch the previous sibling as well
                // unless the previous sibling does not exist then we just use the content as is
                if (content.length <= 8 && prevSibling.previousSibling) {
                    content = prevSibling.previousSibling?.textContent + content;
                }

                // trim spaces, split to words and reverse the order
                const words = content
                    .replace(/^\s+|\s+$/gm, '')
                    .split(' ')
                    .reverse();

                // create the snippet
                for (let word of words) {
                    if (snippet.length + word.length >= dataConfig.citationSnippetLength) break;
                    snippet = word + ' ' + snippet;
                }
            }

            let numbering = dataConfig.enumeration[key];
            if (numbering === undefined) {
                let key_1 = ~~(key / dataConfig.enumeration.length) - 1;
                let key_2 = key % dataConfig.enumeration.length;
                numbering = `${dataConfig.enumeration[key_1]}${dataConfig.enumeration[key_2]}`;
            }

            return `${numbering} [...] ${snippet}`;
        },

        session: () => {
            let dataSource = document.getElementById(this.config.selectors.dataScriptId);
            if (dataSource) {
                const data = JSON.parse(dataSource.innerHTML);

                try {
                    sessionStorage.setItem('doi', data.doi);
                } catch (e) {
                    console.debug('Error saving DOI in session storage');
                }

                try {
                    let source = this.article.querySelector(this.config.selectors.title);
                    if (data.bookTitle) {
                        sessionStorage.setItem('title', data.bookTitle + ':&nbsp;' + source.innerHTML);
                    } else {
                        sessionStorage.setItem('title', source.innerHTML);
                    }
                } catch (e) {
                    console.debug('Error saving document title in session storage');
                }
            } else {
                console.debug('No data script has been sent');
            }

            try {
                let source = this.article.querySelector(this.config.selectors.access);
                sessionStorage.setItem('access', source?.getAttribute('data-article-access'));
            } catch (e) {
                console.debug('Error saving access in session storage');
            }

            try {
                let source = this.article.querySelector(this.config.selectors.accessType);
                sessionStorage.setItem('accessType', source?.getAttribute('data-article-access-type'));
            } catch (e) {
                console.debug('Error saving access type in session storage');
            }
        },
    };

    defaultProcessorTitleCustom(obj, title, type) {
        return title;
    }
    defaultProcessorDescriptionCustom(obj, description, type) {
        return description;
    }

    _processData() {
        return Object.keys(this._processors).reduce((acc, i) => {
            acc[i] = this._processors[i](this.article, acc, i);
            return acc;
        }, {});
    }
}
